import React, {Fragment} from 'react';
import {Box, Typography} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {useNavigate} from 'react-router-dom';

import {TelehealthBookingEnum} from 'presentation/constants';
import {
	BookingTypeEnum,
	FlowEnum,
	TELEMEDICINE_INFO,
	TelemedicineEnum,
} from 'presentation/pages/private/booking/constants';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';

import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';

import {UnitCard} from '../UnitCard';
import {TelemedicineCard} from '../UnitCard/components/TelemedicineCard';
import {HandleSelectCardParams, UnitGridProps} from './types';
import {Units} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';
import {normalizeText} from 'presentation/utils/normalizeText';

const {resultFound, telemedicine} = translations['pt-br'].commons;

export const UnitGrid = ({
	list,
	handleNext,
	flowType,
	showDistanceReference,
	isNotSelfNetwork = false,
	hideFilterResultTitle,
	children,
}: UnitGridProps) => {
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const navigate = useNavigate();
	const {
		SEARCH_PARAMS: {query},
		clearSearch,
	} = useQueryParams();
	const {specialtyName, isFavoriteDoctorsFlow} = bookingData;
	const showResultFoundTitle = query && !hideFilterResultTitle;

	const telemedicineUnit = list?.find(
		item => (item as Units<FlowEnum.HEALTH>)?.telemedicine === TelemedicineEnum.IS_TELEMEDICINE,
	);
	const unitListExcludingTelemedicine = list?.filter(
		item => (item as Units<FlowEnum.HEALTH>)?.telemedicine !== TelemedicineEnum.IS_TELEMEDICINE,
	);

	const handleSelectCard = (params: HandleSelectCardParams) => {
		if (isNotSelfNetwork) {
			clearSearch();
			return;
		}

		if (params.isTelemedicine) {
			setBookingData(prev => ({
				...prev,
				unitCode: params.code,
				unitName: TELEMEDICINE_INFO.name,
				unitAddress: '',
				ufCode: TELEMEDICINE_INFO.ufCode,
				ufName: TELEMEDICINE_INFO.ufName,
				cityName: TELEMEDICINE_INFO.cityName,
				bookingRedirect: true,
				bookingType: BookingTypeEnum.TELEHEALTH,
			}));

			const {PROFESSIONAL_STEP} = getStepPosition({
				flowType: FlowEnum.TELEHEALTH,
				isManualLocation: bookingData.isManualLocation,
			});

			setActiveBookingFlowStep(PROFESSIONAL_STEP);
			navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
			clearSearch();
			return;
		}

		setBookingData(prev => ({
			...prev,
			unitCode: params.code,
			unitName: params.name,
			unitAddress: params.address,
			bookingRedirect: false,
		}));

		if (
			isFavoriteDoctorsFlow &&
			(flowType === FlowEnum.HEALTH || flowType === FlowEnum.TELEHEALTH)
		) {
			setBookingData(prev => ({
				...prev,
				ufCode: params?.ufCode || prev.ufCode,
				ufName: params?.ufName || prev.ufName,
				cityName: params?.cityName || prev.cityName,
			}));
		}

		if (flowType === FlowEnum.ODONTO) {
			setBookingData(prev => ({
				...prev,
				odontoUnitDistrictSelected: params.district,
			}));
		}

		clearSearch();
		handleNext();
	};

	return (
		<Fragment>
			{showResultFoundTitle ? (
				<Typography variant="subtitle2" color="text.secondary" sx={{mb: -1}}>
					{resultFound}
				</Typography>
			) : null}

			{telemedicineUnit ? (
				<Box>
					<Typography
						variant="subtitle1"
						color="text.secondary"
						sx={{mb: 1}}
						fontWeight={600}>
						{telemedicine}
					</Typography>
					<Grid2 container spacing={2}>
						<Grid2 size={{xs: isMobile ? 12 : 6}}>
							<TelemedicineCard
								onClick={() =>
									handleSelectCard({
										code: telemedicineUnit?.code,
										isTelemedicine: true,
									})
								}
								query={query}
							/>
						</Grid2>
					</Grid2>
				</Box>
			) : null}

			{children}

			{unitListExcludingTelemedicine && unitListExcludingTelemedicine?.length > 0 ? (
				<Grid2 container spacing={2}>
					{unitListExcludingTelemedicine?.map((item, index) => {
						const formattedUnitName = normalizeText(item?.name);

						return (
							<Grid2
								key={`${item.name}-${item.formattedAddress}${index}`}
								size={{xs: isMobile ? 12 : 6}}>
								<UnitCard
									showDetailsDataTagId={`${AppointmentBookingEnum.CLICK_DETALHES_UNIDADE}${formattedUnitName}`}
									callDataTagId={`${AppointmentBookingEnum.CLICK_LIGAR_UNIDADE}${formattedUnitName}`}
									bookDataTagId={`${AppointmentBookingEnum.CLICK_AGENDAR_UNIDADE}${formattedUnitName}`}
									seeMapDataTagId={`${AppointmentBookingEnum.CLICK_VER_MAPA_AGENDAMENTO}${formattedUnitName}`}
									isNotSelf={isNotSelfNetwork}
									query={query}
									showDistanceReference={showDistanceReference}
									onClick={() =>
										handleSelectCard({
											district: item?.district,
											code: item?.code,
											address: item?.formattedAddress,
											name: item?.name,
											isTelemedicine: false,
											phoneNumbers: (item as Units<FlowEnum.HEALTH>)
												?.phoneNumbers,
											cityName: item?.city,
											ufName: item?.stateName,
											ufCode: item?.stateCode,
										})
									}
									unitName={item?.name}
									address={item?.formattedAddress}
									phoneNumbers={(item as Units<FlowEnum.HEALTH>)?.phoneNumbers}
									nmBairroOrigem={
										(item as Units<FlowEnum.HEALTH>)?.nmBairroOrigem
									}
									isTelemedicine={false}
									distanceReference={(item as Units<FlowEnum.HEALTH>)?.distance}
									specialtyName={specialtyName}
								/>
							</Grid2>
						);
					})}
				</Grid2>
			) : null}
		</Fragment>
	);
};
